import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Collector, CollectorStatus } from '@/domain/collectors/collector'
import { getIvaConditionOptions, IvaCondition } from '@/domain/invoices/invoice'
import { DocType, getDocTypeOptions } from '@/domain/shared'
import { hashDocNumber } from '@/lib/crypto'
import { emailSchema } from '@/utils/schemas'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

const collectorFormSchema = z.object({
  name: z.string().min(1, 'El nombre es requerido'),
  code: z.number().min(1, 'El codigo es requerido'),
  email: emailSchema,
  docNumber: z.string().optional(),
  docType: z.enum(DocType).optional(),
  phone: z.string().optional(),
  address: z.string().optional(),
  postalCode: z.string().optional(),
  ivaCondition: z.enum(IvaCondition).optional(),
  sellCommission: z.number().min(0).max(100),
  commission: z.number().min(0).max(100),
  status: z.enum(CollectorStatus),
})

type CollectorFormValues = z.infer<typeof collectorFormSchema>

interface CollectorDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  collector: Collector | null
  onSubmit: (data: Partial<Collector> & { email?: string; password?: string }) => void
  isPending: boolean
}

export const CollectorDialog = ({ open, onOpenChange, collector, onSubmit, isPending }: CollectorDialogProps) => {
  const isEditing = !!collector

  const form = useForm<CollectorFormValues>({
    resolver: zodResolver(collectorFormSchema),
    defaultValues: {
      name: '',
      code: 1,
      email: '',
      docNumber: undefined,
      docType: undefined,
      phone: undefined,
      address: undefined,
      postalCode: undefined,
      ivaCondition: undefined,
      sellCommission: 0,
      commission: 0,
      status: CollectorStatus.Active,
    },
  })

  useEffect(() => {
    if (open) {
      if (collector) {
        form.reset({
          name: collector.name,
          code: collector.code,
          email: collector.email || '',
          docNumber: collector.docNumber || undefined,
          docType: collector.docType,
          phone: collector.phone || undefined,
          address: collector.address || undefined,
          postalCode: collector.postalCode || undefined,
          ivaCondition: collector.ivaCondition,
          sellCommission: collector.sellCommission,
          commission: collector.commission,
          status: collector.status,
        })
      } else {
        form.reset({
          name: '',
          code: 1,
          email: '',
          docNumber: undefined,
          docType: undefined,
          phone: undefined,
          address: undefined,
          postalCode: undefined,
          ivaCondition: undefined,
          sellCommission: 0,
          commission: 0,
          status: CollectorStatus.Active,
        })
      }
    }
  }, [open, collector, form])

  const handleSubmit = async (data: CollectorFormValues) => {
    const submitData: Partial<Collector> & { email?: string; password?: string } = {
      name: data.name,
      code: data.code,
      email: data.email,
      docNumber: data.docNumber,
      docType: data.docType,
      phone: data.phone,
      address: data.address,
      postalCode: data.postalCode,
      ivaCondition: data.ivaCondition,
      sellCommission: data.sellCommission,
      commission: data.commission,
      status: data.status,
    }

    if (!isEditing && data.docNumber) {
      submitData.password = await hashDocNumber(data.docNumber)
    }

    onSubmit(submitData)
  }

  const ivaOptions = getIvaConditionOptions()
  const docOptions = getDocTypeOptions()

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Colaborador' : 'Nuevo Colaborador'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos del colaborador.' : 'Crea un nuevo colaborador del sistema.'}
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nombre</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="code"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Codigo</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input placeholder="" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="docType"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Tipo Documento</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar tipo" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {docOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="docNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nro Documento</FormLabel>
                    <FormControl>
                      <Input placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="phone"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Telefono</FormLabel>
                    <FormControl>
                      <Input placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="ivaCondition"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Condicion IVA</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Seleccionar condicion" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {ivaOptions.map((option) => (
                          <SelectItem key={option.value} value={option.value}>
                            {option.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Direccion</FormLabel>
                    <FormControl>
                      <Input placeholder="" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="postalCode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Codigo Postal</FormLabel>
                    <FormControl>
                      <Input placeholder="CP" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="sellCommission"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>% Venta</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="commission"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>% Cobro</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} onChange={(e) => field.onChange(Number(e.target.value))} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="status"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Estado</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar estado" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value={CollectorStatus.Active}>Activo</SelectItem>
                      <SelectItem value={CollectorStatus.Inactive}>Inactivo</SelectItem>
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between space-x-2 pt-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancelar
              </Button>
              <Button type="submit" disabled={isPending}>
                {isPending ? 'Guardando...' : isEditing ? 'Actualizar' : 'Crear'}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
